import React, { useState, useEffect, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Plus, Send, PlayCircle, ShieldAlert, ShieldCheck, Trash2 } from "lucide-react";
import { fechaHoraCompleta } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

// Prompt L (2026-10-01): destinatarios de los correos de la plataforma (Resend).
const TIPOS = [
  { k: "reportes", get l() { return t("Reportes"); } },
  { k: "problemas", get l() { return t("Aviso de problema"); } },
  { k: "resumen", get l() { return t("Resumen diario"); } },
];

const TIPO_ENVIO_LABEL = {
  get reporte() { return t("Reporte"); },
  get problema() { return t("Aviso de problema"); },
  get resumen() { return t("Resumen diario"); },
  get resumen_manual() { return t("Resumen (a mano)"); },
  get prueba() { return t("Prueba"); },
};

const ESTADO_ENVIO_LABEL = { get enviado() { return t("Enviado"); }, get error() { return t("Error"); }, get procesando() { return t("Enviando"); } };

export default function CorreosConfig() {
  const [conf, setConf] = useState(null);
  const [envios, setEnvios] = useState([]);
  const [nuevo, setNuevo] = useState("");
  const [saving, setSaving] = useState(false);
  const [accion, setAccion] = useState("");

  const cargar = useCallback(async () => {
    const [c, e] = await Promise.all([api.get("/correos/config"), api.get("/correos/envios")]);
    setConf(c.data);
    setEnvios(e.data);
  }, []);

  useEffect(() => { cargar().catch(() => {}); }, [cargar]);

  const setDest = (i, campo, valor) => setConf((c) => ({
    ...c, destinatarios: c.destinatarios.map((d, j) => (j === i ? { ...d, [campo]: valor } : d)),
  }));

  const agregar = () => {
    const email = nuevo.trim().toLowerCase();
    if (!email) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error(t("Correo inválido")); return; }
    if (conf.destinatarios.some((d) => d.email === email)) { setNuevo(""); return; }
    setConf((c) => ({ ...c, destinatarios: [...c.destinatarios, { email, reportes: true, problemas: true, resumen: true }] }));
    setNuevo("");
  };

  const quitar = (i) => setConf((c) => ({ ...c, destinatarios: c.destinatarios.filter((_, j) => j !== i) }));

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/correos/config", { destinatarios: conf.destinatarios, hora_resumen: conf.hora_resumen, idioma: conf.idioma });
      setConf(data);
      toast.success(t("Correos guardados"));
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const probar = async () => {
    setAccion("prueba");
    try {
      const { data } = await api.post("/correos/prueba", {});
      const errores = data.resultados.filter((r) => r.estado !== "enviado");
      if (errores.length === 0) toast.success(t("Correo de prueba enviado a {length} destinatario(s)", { length: data.resultados.length }));
      else toast.error(`${errores[0].email}: ${errores[0].detalle || t("no se pudo enviar")}`);
      await cargar();
    } catch (e) { toast.error(apiError(e)); } finally { setAccion(""); }
  };

  const resumenAhora = async () => {
    setAccion("resumen");
    try {
      const { data } = await api.post("/correos/resumen-ahora");
      if (data.motivo) toast.error(data.motivo);
      else toast.success(t("Resumen enviado: {v0} · Errores: {v1}", { v0: data.enviado || 0, v1: data.error || 0 }));
      await cargar();
    } catch (e) { toast.error(apiError(e)); } finally { setAccion(""); }
  };

  if (!conf) return <div className="p-6"><Loader2 className="h-6 w-6 animate-spin text-ink-3" /></div>;

  return (
    <div className="space-y-6">
      <div className={cn("rounded-xl border-2 p-4 flex items-start gap-3", conf.configurado ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50")} data-testid="correos-status">
        {conf.configurado ? <ShieldCheck className="h-5 w-5 text-[#277A3F] shrink-0 mt-0.5" /> : <ShieldAlert className="h-5 w-5 text-[#B07A12] shrink-0 mt-0.5" />}
        <div className="min-w-0">
          <p className={cn("text-sm font-bold", conf.configurado ? "text-emerald-800" : "text-amber-800")}>
            {conf.configurado ? t("Correo conectado (Resend)") : t("Falta la API key de Resend (variable RESEND_API_KEY)")}
          </p>
          <p className="text-xs text-ink-2 mt-0.5">{t("Los correos salen desde ")}<span className="font-mono break-all" style={{ whiteSpace: "normal" }}>{conf.remitente}</span></p>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6 space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Mail className="h-5 w-5 text-ink" />
            <h2 className="font-display font-semibold text-[18px] text-ink">{t("Destinatarios")}</h2>
          </div>
          <p className="text-sm text-ink-3">{t("Elige quién recibe cada correo de la plataforma.")}</p>
        </div>

        <div className="flex gap-2">
          <Input
            data-testid="correos-nuevo-input"
            type="email"
            value={nuevo}
            onChange={(e) => setNuevo(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregar(); } }}
            placeholder={t("correo@empresa.com")}
            className="h-11 flex-1"
          />
          <Button type="button" data-testid="correos-agregar" variant="outline" className="h-11" onClick={agregar}><Plus className="h-4 w-4 mr-1" />{t(" Agregar")}</Button>
        </div>

        <div className="border rounded-lg divide-y" data-testid="correos-destinatarios-tabla">
          {conf.destinatarios.length === 0 && <p className="px-3 py-6 text-center text-sm text-ink-3">{t("Sin destinatarios")}</p>}
          {conf.destinatarios.map((d, i) => (
            <div key={d.email} data-testid={`correos-fila-${d.email}`} className="px-3 py-3 flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4">
              <div className="flex items-center justify-between gap-2 lg:flex-1 min-w-0">
                <p className="font-medium text-ink truncate" title={d.email}>{d.email}</p>
                <Button type="button" variant="ghost" size="sm" className="lg:hidden" data-testid={`correos-quitar-m-${d.email}`} onClick={() => quitar(i)} aria-label={t("Quitar {email}", { email: d.email })}><Trash2 className="h-4 w-4 text-danger" /></Button>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {TIPOS.map((tt) => (
                  <label key={tt.k} className="flex items-center gap-2 text-sm text-ink-2 min-h-[32px] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`correos-${tt.k}-${d.email}`}
                      checked={!!d[tt.k]}
                      onChange={(e) => setDest(i, tt.k, e.target.checked)}
                      className="h-5 w-5 accent-[#16181D]"
                    />
                    {tt.l}
                  </label>
                ))}
              </div>
              <Button type="button" variant="ghost" size="sm" className="hidden lg:inline-flex" data-testid={`correos-quitar-${d.email}`} onClick={() => quitar(i)} aria-label={t("Quitar {email}", { email: d.email })}><Trash2 className="h-4 w-4 text-danger" /></Button>
            </div>
          ))}
        </div>

        <div className="max-w-[220px]">
          <Label className="eyebrow">{t("Hora del resumen diario")}</Label>
          <Input data-testid="correos-hora-resumen" type="time" value={conf.hora_resumen} onChange={(e) => setConf((c) => ({ ...c, hora_resumen: e.target.value }))} className="mt-1 h-11" />
        </div>

        <div className="max-w-[220px]">
          <Label className="eyebrow">{t("Idioma de los correos")}</Label>
          <select data-testid="correos-idioma" value={conf.idioma || "en"} onChange={(e) => setConf((c) => ({ ...c, idioma: e.target.value }))} className="mt-1 h-11 w-full rounded-md border border-line bg-white px-3 text-sm">
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
          <p className="text-xs text-ink-3 mt-1">{t("Se usa en el resumen diario y en el aviso de problema.")}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button data-testid="correos-guardar" onClick={guardar} disabled={saving} className="h-11 bg-brand hover:bg-brand-dark text-white px-8">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Guardar")}
          </Button>
          <Button data-testid="correos-prueba" variant="outline" className="h-11" onClick={probar} disabled={!!accion}>
            {accion === "prueba" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" />{t(" Enviar correo de prueba")}</>}
          </Button>
          <Button data-testid="correos-resumen-ahora" variant="outline" className="h-11" onClick={resumenAhora} disabled={!!accion}>
            {accion === "resumen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlayCircle className="h-4 w-4 mr-2" />{t(" Enviar resumen de hoy")}</>}
          </Button>
        </div>
        <p className="text-xs text-ink-3">{t("La prueba y el resumen se mandan a los destinatarios guardados.")}</p>
      </div>

      <div className="bg-white rounded-xl border p-6">
        <h2 className="font-display font-semibold text-[18px] text-ink mb-4">{t("Últimos envíos")}</h2>
        <div className="border rounded-lg overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full min-w-[640px] text-sm" data-testid="correos-envios-tabla">
            <thead className="bg-paper sticky top-0">
              <tr className="text-left eyebrow">
                <th className="px-3 py-2">{t("Tipo")}</th>
                <th className="px-3 py-2">{t("Para")}</th>
                <th className="px-3 py-2">{t("Estado")}</th>
                <th className="px-3 py-2">{t("Detalle")}</th>
                <th className="px-3 py-2">{t("Fecha")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {envios.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-ink-3">{t("Sin envíos todavía")}</td></tr>}
              {envios.map((e) => (
                <tr key={e.id} data-testid={`correos-envio-${e.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{TIPO_ENVIO_LABEL[e.tipo] || e.tipo}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{e.destinatario}</td>
                  <td className={cn("px-3 py-2 font-medium", e.estado === "error" ? "text-danger" : "text-ink")}>{ESTADO_ENVIO_LABEL[e.estado] || e.estado}</td>
                  <td className="px-3 py-2 text-ink-3 max-w-[280px] break-words">{e.detalle || "—"}</td>
                  <td className="px-3 py-2 text-ink-3 whitespace-nowrap">{fechaHoraCompleta(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
